import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPropertyCount, recalculatePropertyCounts } from '../services/propertyService';
import { getAllSources } from '../services/sourceService';
import { Button, Card, CardContent, CardFooter, Toast } from './ui';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    sources: 0,
    properties: 0,
    activeProperties: 0,
    inactiveProperties: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', variant: 'success' as 'success' | 'error' });

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [sourcesData, propertyCounts] = await Promise.all([
          getAllSources(),
          getPropertyCount(),
        ]);

        setCounts({
          sources: sourcesData.length,
          properties: propertyCounts.total,
          activeProperties: propertyCounts.active,
          inactiveProperties: propertyCounts.inactive,
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCounts();
  }, []);

  const handleRecalculateCounts = async () => {
    setRecalculating(true);
    try {
      const result = await recalculatePropertyCounts();
      setCounts(prev => ({
        ...prev,
        properties: result.counts.total,
        activeProperties: result.counts.active,
        inactiveProperties: result.counts.inactive,
      }));
      setToast({ open: true, message: result.message, variant: 'success' });
    } catch (error) {
      console.error('Error recalculating counts:', error);
      setToast({ open: true, message: 'Failed to recalculate counts', variant: 'error' });
    } finally {
      setRecalculating(false);
    }
  };

  const cards = [
    {
      title: 'Sources',
      count: counts.sources,
      description: 'Manage your property data sources',
      action: () => navigate('/sources')
    },
    {
      title: 'Total Properties',
      count: counts.properties,
      description: `Active: ${counts.activeProperties} | Inactive: ${counts.inactiveProperties}`,
      action: () => navigate('/properties'),
      secondaryAction: {
        label: 'Recalculate',
        handler: handleRecalculateCounts,
        loading: recalculating
      }
    }
  ];

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardContent className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-900">{card.title}</h2>
              <p className="text-5xl font-bold text-gray-600">
                {loading ? '...' : card.count}
              </p>
              <p className="text-sm text-gray-500">{card.description}</p>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button size="sm" onClick={card.action}>
                Manage {card.title}
              </Button>
              {card.secondaryAction && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={card.secondaryAction.handler}
                  loading={card.secondaryAction.loading}
                  disabled={card.secondaryAction.loading}
                >
                  {card.secondaryAction.label}
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>

      <Toast
        open={toast.open}
        message={toast.message}
        variant={toast.variant}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default Dashboard;
